// InvitationsCustomization.jsx
import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import { FileInput, Button, Control, Field, Icon, Switch } from "../design";
import "./workspaceModals.css";
import { haveValue, toast } from "../../helper/common";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";

const InvitationsCustomization = ({
  // --- EDIT mode props ---
  workspaceData,
  onClose,
  onEditBeneficiary,
  // --- CREATE mode props ---
  actionType = "edit", // "edit" | "create"
  invitationWorkspaceData,
  setInvitationWorkspaceData,
  handleSubmitFromParent,
  disableButtons,
  handleGoBackToName,
}) => {
  const { _t } = useTranslator();
  const { user, loadUserTenant } = useContext(AuthContext);
  const {
    updateInvitation,
    checkForDuplicateInvitationSlug,
    deleteInvitationLogo,
    deaWorkspace,
  } = useContext(LandLordContext);

  const isCreateMode = actionType === "create";

  const baseUrl = `${window.location.host}/oauth/`;
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const debounceRef = useRef(null);

  const SLUG_MIN_CHARACTERS = 3;
  const SLUG_MAX_CHARACTERS = 15;

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  const [customLinkEnabled, setCustomLinkEnabled] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.customLinkEnabled ?? false)
      : (workspaceData?.customLinkEnabled ?? false)
  );

  const [protectedHousing, setProtectedHousing] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.protectedHousing ?? false) // was hardcoded false
      : (user?.current_workspace?.protected_housing_enabled ?? false)
  );

  const [affidavitActivated, setAffidavitActivated] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.affidavitActivated ?? false) // was hardcoded false
      : (user?.current_workspace?.affidavit_activated ?? false)
  );

  const [deaRulesActivated, setDeaRulesActivated] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.dea_rules_activated ?? false) // was hardcoded false
      : (user?.current_workspace?.dea_rules_activated ?? false)
  );

  const [insuranceEnabled, setInsuranceEnabled] = useState(
    user?.current_workspace?.insurance_service_status ?? false
  );
  const [skorProtectEnabled, setSkorProtectEnabled] = useState(
    user?.current_workspace?.skor_protect_service ?? false
  );

  const [slug, setSlug] = useState(
    isCreateMode
      ? (invitationWorkspaceData?.customLinkSlug ?? "")
      : workspaceData?.customLinkSlug
  );

  const [slugError, setSlugError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [imageDeleteLoading, setImageDeleteLoading] = useState(false);

  const initialProtectedHousing =
    user?.current_workspace?.protected_housing_enabled ?? false;
  const initialAffidavitActivated =
    user?.current_workspace?.affidavit_activated ?? false;
  const initialDeaRulesActivated =
    user?.current_workspace?.dea_rules_activated ?? false;
  const initialInsuranceEnabled =
    user?.current_workspace?.insurance_service_status ?? false;
  const initialSkorProtectEnabled =
    user?.current_workspace?.skor_protect_service ?? false;

  // ── Slug change handler ────────────────────────────────────────────────────
  const handleChange = (e) => {
    let sanitized = e.target.value.replace(/[^a-zA-Z0-9-_]/g, "");
    if (sanitized.length > SLUG_MAX_CHARACTERS) {
      sanitized = sanitized.slice(0, SLUG_MAX_CHARACTERS);
    }
    setSlug(sanitized);
    setSlugTouched(true);
    if (sanitized !== slug) setSlugAvailable(null);
    setSlugChecking(false);
    setSlugError("");
  };

  // ── Slug debounce / validation ─────────────────────────────────────────────
  useEffect(() => {
    if (!customLinkEnabled || !slugTouched) return;

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!slug) {
        setSlugError(_t("enterSlugToContinue"));
        setSlugAvailable(null);
        return;
      }
      if (slug.length < SLUG_MIN_CHARACTERS) {
        setSlugError(_t("slugMinCharactersLimitNotMatched"));
        setSlugAvailable(null);
        return;
      }
      if (slug.length > SLUG_MAX_CHARACTERS) {
        setSlugError(_t("slugMaxCharactersLimitReached"));
        setSlugAvailable(null);
        return;
      }

      // In edit mode skip API call if slug unchanged
      const existingSlug = isCreateMode
        ? invitationWorkspaceData?.customLinkSlug
        : workspaceData?.customLinkSlug;

      if (slug === existingSlug) {
        setSlugError("");
        setSlugAvailable(true);
        return;
      }

      try {
        setSlugChecking(true);
        setSlugError("");
        const res = await checkForDuplicateInvitationSlug({
          customLinkSlug: slug,
        });
        if (res?.success) {
          setSlugAvailable(true);
        } else {
          setSlugError(_t("slugAlreadyInSystemPleaseTrySomethingElse"));
          setSlugAvailable(false);
        }
      } catch {
        setSlugError(_t("slugAlreadyInSystemPleaseTrySomethingElse"));
        setSlugAvailable(false);
      } finally {
        setSlugChecking(false);
      }
    }, 600);

    return () => clearTimeout(debounceRef.current);
  }, [slug, customLinkEnabled]);

  // ── File helpers ───────────────────────────────────────────────────────────
  const validateFile = async (file) => {
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      setError(_t("fileTypeNotAllowed", "Only PNG or JPG files allowed"));
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError(_t("fileUploadedExceedsTwoMbLimit", "File exceeds 2MB limit"));
      return false;
    }
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Corrupted image"));
        img.src = URL.createObjectURL(file);
      });
    } catch {
      setError(
        _t("isCorruptedOrUnreadableFile", "File is corrupted or unreadable")
      );
      return false;
    }
    setError("");
    return true;
  };

  const processFile = async (file) => {
    if (!file) return;
    const isValid = await validateFile(file);
    if (isValid) setSelectedFile(file);
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError("");
  };

  // ── Change detection ───────────────────────────────────────────────────────
  const hasChanges = isCreateMode
    ? true // always allow continuing in create mode
    : !!selectedFile ||
      customLinkEnabled !== workspaceData?.customLinkEnabled ||
      slug !== workspaceData?.customLinkSlug ||
      protectedHousing !== initialProtectedHousing ||
      affidavitActivated !== initialAffidavitActivated ||
      deaRulesActivated !== initialDeaRulesActivated ||
      insuranceEnabled !== initialInsuranceEnabled ||
      skorProtectEnabled !== initialSkorProtectEnabled;

  const canSubmit =
    !error &&
    hasChanges &&
    !slugChecking &&
    (!customLinkEnabled || slugAvailable !== false);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (customLinkEnabled) {
      if (!slug) {
        setSlugError(_t("enterSlugToContinue"));
        return;
      }
      if (slug.length < SLUG_MIN_CHARACTERS) {
        setSlugError(_t("slugMinCharactersLimitNotMatched"));
        return;
      }
      if (slug.length > SLUG_MAX_CHARACTERS) {
        setSlugError(_t("slugMaxCharactersLimitReached"));
        return;
      }
    }

    // ── CREATE mode: push data up to parent, then advance step ──────────────
    if (isCreateMode) {
      setInvitationWorkspaceData({
        customLinkEnabled,
        customLinkSlug: customLinkEnabled ? slug : "",
        invitationLogo: selectedFile,
        protectedHousing, // ← add this
        affidavitActivated, // ← add this
        dea_rules_activated: deaRulesActivated,
      });
      handleSubmitFromParent();
      return;
    }

    // ── EDIT mode: call API directly ─────────────────────────────────────────
    try {
      setSubmitLoading(true);
      const formData = new FormData();
      if (selectedFile) formData.append("invitationLogo", selectedFile);
      formData.append("customLinkEnabled", customLinkEnabled ?? false);
      formData.append("affidavit_activated", affidavitActivated);
      formData.append("dea_rules_activated", deaRulesActivated);
      formData.append("protected_housing_enabled", protectedHousing);
      if (!isCreateMode && user?.insurance_service_status) {
        formData.append("insurance_service_status", insuranceEnabled);
      }
      if (!isCreateMode && user?.skor_protect_service) {
        formData.append("skor_protect_service", skorProtectEnabled);
      }
      if (customLinkEnabled) formData.append("customLinkSlug", slug);

      await updateInvitation(formData);
      await loadUserTenant("landlord");
      onClose();
      toast.success(_t("invitationSettingsUpdatedSuccessfully"));
    } catch (err) {
      console.error("Error uploading:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteInvitationLink = async () => {
    if (imageDeleteLoading) return;
    try {
      setImageDeleteLoading(true);
      await deleteInvitationLogo();
      await loadUserTenant("landlord");
      toast.success(_t("invitationLogoRemovedSuccessfully"));
    } catch {
      toast.error(_t("failedToRemoveInvitationLogo"));
    } finally {
      setImageDeleteLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  // In create mode the component is rendered inline (no Modal wrapper needed).
  // In edit mode it sits inside the "Manage workspace" tab with a Save button.
  const existingLogo =
    !isCreateMode && haveValue(workspaceData?.invitationLogo);

  const body = (
    <div className="invitation-tab">
      {/* Custom Invitation Link */}
      <Switch
        id="custom-invitation-link"
        label={_t("customInvitationLink")}
        description={
          customLinkEnabled
            ? _t("useABrandedUrlSlugWhenInvitingTenants")
            : undefined
        }
        checked={customLinkEnabled}
        onChange={() => setCustomLinkEnabled((v) => !v)}
      >
        {customLinkEnabled && (
          <Field
            className="invitation-url-field"
            htmlFor="custom-invitation-slug"
            error={slugError || undefined}
          >
            <Control>
              <span className="invitation-url-prefix">{baseUrl}</span>
              <input
                id="custom-invitation-slug"
                type="text"
                value={slug}
                onChange={handleChange}
              />
              {slugChecking && (
                <LoadingSpinner
                  size={"16px"}
                  color="#253837"
                  borderWidth={1}
                  className="docs-loading-spinner"
                />
              )}
              {!slugChecking &&
                slugTouched &&
                slugAvailable !== null &&
                slug.length >= SLUG_MIN_CHARACTERS && (
                  <span
                    className={`invitation-url-status ${
                      slugAvailable ? "is-ok" : "is-taken"
                    }`}
                  >
                    <Icon name={slugAvailable ? "check" : "close"} size={18} />
                  </span>
                )}
            </Control>
          </Field>
        )}
      </Switch>

      {/* Invitation Logo */}
      <Field
        className="invitation-logo-field"
        label={
          <>
            {_t("invitationLogo")}{" "}
            <span className="invitation-optional">{_t("optional")}</span>
          </>
        }
        error={error || undefined}
      >
        <p className="invitation-hint">
          {_t("shownToTenantsWhenTheyReceiveAnInvitation")}
        </p>
        {existingLogo ? (
          <div className="invitation-logo-preview">
            <img
              height={100}
              width={100}
              src={`${workspaceData?.invitationLogo}`}
              alt=""
            />
            <Button
              variant="secondary"
              size="sm"
              className="invitation-logo-delete"
              aria-label={_t("remove")}
              loading={imageDeleteLoading}
              onClick={handleDeleteInvitationLink}
            >
              <Icon name="close" size={14} />
            </Button>
          </div>
        ) : (
          <div
            className={`invitation-drop ${error ? "has-error" : ""}`}
            onClick={() => !selectedFile && fileInputRef.current.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <FileInput
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/png,image/jpeg,image/jpg"
              hidden
            />

            {selectedFile ? (
              <div className="invitation-file">
                <img src={URL.createObjectURL(selectedFile)} alt="" />
                <span className="invitation-file__text">
                  <span className="invitation-file__name">
                    {selectedFile.name}
                  </span>
                  <span className="invitation-file__size">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={_t("removeFile")}
                  onClick={removeFile}
                >
                  <Icon name="close" size={16} />
                </Button>
              </div>
            ) : (
              <>
                <span className="invitation-drop__icon">
                  <Icon name="upload" />
                </span>
                <p className="invitation-drop__title">
                  {_t("dragAndDropYourLogoHere")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current.click();
                  }}
                >
                  {_t("selectFromFiles")}
                </Button>
                <p className="invitation-hint">
                  {_t("pngJpgUpTo2MbRecommended256X256")}
                </p>
              </>
            )}
          </div>
        )}
      </Field>

      <Switch
        id="protected-housing"
        label={_t("protectedHousing")}
        description={_t("protectedHousingTogglerDescriptionInInvitationPopup")}
        checked={protectedHousing}
        onChange={() => setProtectedHousing((v) => !v)}
      />

      <Switch
        id="funds-affidavit"
        label={_t("fundsDeclarationAffidavit")}
        description={_t(
          "fundsDeclarationAffidavitTogglerDescriptionInInvitationPopup"
        )}
        checked={affidavitActivated}
        onChange={() => setAffidavitActivated((v) => !v)}
      />

      {/* Insurance — only shown in edit mode */}
      {!isCreateMode && user?.insurance_service_status && (
        <Switch
          id="insurance"
          label={_t("insuranceToggle")}
          description={_t("insuranceTogglerDescriptionInInvitationPopup")}
          checked={insuranceEnabled}
          onChange={() => {
            const next = !insuranceEnabled;
            setInsuranceEnabled(next);
            if (!next) setSkorProtectEnabled(false);
          }}
        >
          {insuranceEnabled &&
            WORKSPACE_ROLES_CAPABILITIES?.manageBeneficiaryDetails?.includes(
              user?.current_workspace_role
            ) && (
              <Button
                variant="secondary"
                size="sm"
                className="invitation-link-action"
                iconStart={<Icon name="edit" size={14} />}
                onClick={onEditBeneficiary}
              >
                {_t("editBeneficiaryDetails")}
              </Button>
            )}
        </Switch>
      )}

      {/* Skor Protect — only shown in edit mode */}
      {!isCreateMode && user?.skor_protect_service && (
        <Switch
          id="skor-protect"
          label={_t("skorProtectToggle")}
          description={_t("skorProtectTogglerDescriptionInInvitationPopup")}
          checked={skorProtectEnabled}
          onChange={() => {
            const next = !skorProtectEnabled;
            setSkorProtectEnabled(next);
            if (next) setInsuranceEnabled(true);
          }}
        />
      )}

      {/* Toggle for DEA created workspaces only */}
      {((actionType == "create" &&
        user?._id?.toString() === "691368fee4b283b07c7d33c2") ||
        (actionType == "edit" && deaWorkspace)) && (
        <Switch
          id="dea-rules"
          label={_t("enableDeaRules")}
          description={_t("enableDeaRulesTogglerDescriptionInInvitationPopup")}
          checked={deaRulesActivated}
          onChange={() => setDeaRulesActivated((v) => !v)}
        />
      )}
    </div>
  );

  // ── Create mode: render inline with parent-controlled button ───────────────
  if (isCreateMode) {
    return (
      <>
        {body}
        <div className="ds-modal__actions ds-modal__actions--row">
          <Button variant="secondary" size="lg" onClick={handleGoBackToName}>
            {_t("goBack")}
          </Button>
          <Button size="lg" onClick={handleSubmit}>
            {_t("continue")}
          </Button>
        </div>
      </>
    );
  }

  // ── Edit mode: inside the "Manage workspace" tab, with its own Save ───────
  return (
    <>
      {body}
      <div className="ds-modal__actions ds-modal__actions--end">
        <Button
          size="lg"
          disabled={!canSubmit}
          loading={submitLoading}
          onClick={handleSubmit}
        >
          {_t("saveChanges")}
        </Button>
      </div>
    </>
  );
};

export default InvitationsCustomization;
